import React, {PureComponent} from "react";
import {connect, DispatchProp} from "react-redux";
import {IApplicationState} from "../../../../../store";
import {DialogManagerProps} from "../../../../../store/dialog-manager/types";
import {cssHelper} from "../../../../utils/cssHelper";
import pad from "../../../../utils/pad";
import styles from "./no-lives-dialog.module.css";
import dialogStyles from "../dialog.module.css";
import icons from "../../../../styles/icons.module.css";
import {buyLives} from "../../../../../store/player/actions";
import {Overlay} from "../../../overlay/Overlay";
import {iif} from "../../../iif";
import getFontSize from "../../../../utils/getFontSize";
import renderText from "../../../renderText";
import playSound from "../../../../sounds/playSound";
import i18n from "../../../../i18n";

interface NoLivesDialogProps {
    readonly restoreLivesInterval: number;
    readonly lastLiveIncrementTimestamp: number;
    readonly coins: number;
    readonly liveCost: number;
    readonly hasLives: boolean;
    readonly rewardedAdIsAvailable: boolean;
}

interface NoLivesDialogState {
    hours: number;
    minutes: number;
    seconds: number;
    percentage: number;
}

class NoLivesDialog extends PureComponent<NoLivesDialogProps & DialogManagerProps & DispatchProp, NoLivesDialogState> {
    private timer?: number;

    constructor(props: NoLivesDialogProps & DialogManagerProps & DispatchProp, context: any) {
        super(props, context);

        this.state = this.recalculateState();
    }

    render() {
        const {coins, liveCost, rewardedAdIsAvailable} = this.props;

        const fontSize = Math.min(...[
            i18n("gameplay.nolivesleft.watchad"),
            i18n("gameplay.nolivesleft.getlives")
        ].map(text => getFontSize(text.length, 28, 40, 15, 22)));

        const header = this.props.hasLives
            ? i18n("gameplay.livesshop.newfreelifein")
            : i18n("gameplay.nolivesleft.header");

        return (
            <Overlay>
                <div className={dialogStyles.container}>
                    <button className={cssHelper(dialogStyles.back_button, icons.back).toString()} onClick={() => this.props.close()}>✖</button>

                    {renderText(header, 26, 30, 15, 25, styles.header)}

                    <div className={styles.heart_bg}>
                        <i className={styles.heart} style={{width: this.state.percentage + '%'}}/>
                    </div>

                    <table className={styles.timer}>
                        <tbody>
                            <tr>
                                <td>
                                    <div className={styles.timer_value}>{pad(this.state.hours)}</div>
                                    <div className={styles.timer_description}>{i18n("gameplay.nolivesleft.hours")}</div>
                                </td>
                                <td>
                                    <div className={styles.timer_value}>{pad(this.state.minutes)}</div>
                                    <div className={styles.timer_description}>{i18n("gameplay.nolivesleft.minutes")}</div>
                                </td>
                                <td>
                                    <div className={styles.timer_value}>{pad(this.state.seconds)}</div>
                                    <div className={styles.timer_description}>{i18n("gameplay.nolivesleft.seconds")}</div>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div className={dialogStyles.vertical_actions}>
                        {iif(rewardedAdIsAvailable,
                            <button className={cssHelper(dialogStyles.button, dialogStyles.primary_button, styles.action).toString()} onClick={() => this.props.close({ value: 'watchAd' })}>
                                <span style={{fontSize}}>{i18n("gameplay.nolivesleft.watchad")}</span>
                                <span className={styles.icon}><i className={cssHelper(icons.icon, icons.reward).toString()}/></span>
                            </button>
                        )}

                        <button className={cssHelper(dialogStyles.button, dialogStyles.primary_button, styles.action).toString()} onClick={() => this.getLives()} disabled={liveCost > coins}>
                            <span style={{fontSize}}>{i18n("gameplay.nolivesleft.getlives")}</span>
                            <span className={cssHelper(styles.icon, styles.cost).toString()}><i className={cssHelper(icons.icon, icons.coins).toString()}/>{liveCost}</span>
                        </button>
                    </div>
                </div>
            </Overlay>
        );
    }

    componentDidMount(): void {
        this.startTimer();
    }

    componentWillUnmount(): void {
        this.stopTimer();
    }

    private recalculateState(autoSet = false) {
        const {restoreLivesInterval, lastLiveIncrementTimestamp} = this.props;
        const timeToIncrementLives = restoreLivesInterval - (new Date().getTime() - lastLiveIncrementTimestamp);

        const state = {
            hours: Math.floor(timeToIncrementLives / (60 * 60 * 1000)),
            minutes: Math.floor((timeToIncrementLives / (60 * 1000)) % 60),
            seconds: Math.floor((timeToIncrementLives / 1000) % 60),
            percentage: Math.ceil((restoreLivesInterval - timeToIncrementLives) * 100 / restoreLivesInterval)
        };

        if (timeToIncrementLives < 1000) {
            this.props.close();
        } else if (autoSet) {
            this.setState(state);
        }

        return state;
    }

    private startTimer() {
        this.stopTimer();

        this.timer = window.setInterval(() => this.recalculateState(true), 1000);
    }

    private stopTimer() {
        if (this.timer) {
            clearInterval(this.timer);
        }
    }

    private getLives() {
        playSound("item-purchase");
        this.props.dispatch(buyLives(1));
        this.props.close();
    }
}

export default connect(({ player, settings, advertisement }: IApplicationState) => ({
    restoreLivesInterval: settings.gameBalance.restoreLivesInterval,
    lastLiveIncrementTimestamp: player.lastLiveIncrementTimestamp,
    coins: player.coins,
    liveCost: settings.gameBalance.liveCost,
    hasLives: player.lives > 0,
    rewardedAdIsAvailable: advertisement.rewardedAdIsAvailable,
}))(NoLivesDialog);